import type React from "react";
import { ActionIcon, Group, Stack, Text, Tooltip } from "@mantine/core";
import { Table } from "@xplatform/design-system";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { ReviewStatus, type RowEdit } from "@models/job";
import type {
  MappingResultRow,
  MappingResultRowTm,
  TmTarget,
} from "@models/mappingResult";
import type { RowSaveStatus } from "@hooks/useJobResults";
import { useResultRow } from "@hooks/jobResults/useResultRow";
import RevisionHistoryDrawer from "@components/RevisionHistoryDrawer/RevisionHistoryDrawer";
import { PLACEHOLDER, resolveDisplayTarget } from "@utils/rowHelpers";
import RowStatusCell from "../RowStatusCell";
import RowFindingBadge from "../RowFindingBadge";
import TmRowActions from "./TmRowActions";
import TmScoreExamplesTooltip from "./TmScoreExamplesTooltip";

interface TmJobResultsTableRowProps {
  row: MappingResultRowTm;
  edit: RowEdit | undefined;
  saveState: RowSaveStatus | undefined;
  jobId: string;
  expanded: boolean;
  /** The row's candidates, once its dropdown has been opened. Needed here so
   *  an unsaved pick renders before the PATCH lands. */
  candidates: readonly TmTarget[] | undefined;
  registerRef: (el: HTMLTableRowElement | null) => void;
  onToggleExpand: () => void;
  onAccept: (text?: string) => void;
  onReject: () => void;
  onRecover: () => void;
  onRetrySave: () => void;
  onRowRestored: (row: MappingResultRow) => void;
}

/**
 * One technical-manual row: the value as extracted, and the catalogue value it
 * resolved to.
 *
 * No score column. TM's score is a cosine against a value the reviewer can
 * read in full two cells away — it offered a number to argue with rather than
 * evidence to judge by. What stands in its place is the "Exact Match" badge
 * under the value: an arithmetic decision over every value of the field, which
 * is either true or absent and cannot be misread as "61% right".
 */
const TmJobResultsTableRow: React.FC<TmJobResultsTableRowProps> = ({
  row,
  edit,
  saveState,
  jobId,
  expanded,
  candidates,
  registerRef,
  onToggleExpand,
  onAccept,
  onReject,
  onRecover,
  onRetrySave,
  onRowRestored,
}) => {
  const { t } = useTranslation();
  const {
    historyOpen,
    setHistoryOpen,
    effectiveStatus,
    isFailed,
    isSaving,
    actionsDisabled,
  } = useResultRow(row, edit, saveState);

  const { source } = row;
  const target = resolveDisplayTarget(row, edit, candidates);
  const isRejected = effectiveStatus === ReviewStatus.Rejected;

  const rowClass = `job-results-page__row${
    expanded ? " job-results-page__row--expanded" : ""
  }`;

  // Wrapped, not passed straight through: TmRowActions hands its button's
  // click event to the handler, which `onAccept(text?)` would take as the text.
  const handleAccept = (): void => {
    onAccept();
  };

  const dimmed = (
    <Text size="xs" c="dimmed">
      {PLACEHOLDER}
    </Text>
  );

  return (
    <>
      <Table.Tr
        ref={registerRef}
        className={rowClass}
        tabIndex={0}
        data-row-index={row.rowIndex}
      >
        {/* # */}
        <Table.Td className="job-results-page__tm-index-cell">
          <Text
            size="xs"
            c="dimmed"
            ff="monospace"
            fw={500}
            style={{ fontSize: "0.68rem" }}
          >
            {row.rowIndex + 1}
          </Text>
        </Table.Td>

        {/* Where in the manual the value was read from */}
        <Table.Td>
          {source.path ? (
            <Text size="sm" ff="monospace" lineClamp={2} title={source.path}>
              {source.path}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* The extracted value. The cleaned text is what was matched, so it
            leads; the raw extraction sits under it when the two differ. */}
        <Table.Td>
          {isFailed ? (
            <Text size="xs" c="red" lineClamp={2}>
              {row.error?.message ?? t("resultRow.status.failed")}
            </Text>
          ) : source.cleanedText || source.rawText ? (
            <Stack gap={2}>
              <Text
                size="sm"
                c="black"
                style={{ whiteSpace: "normal", wordBreak: "break-word" }}
              >
                {source.cleanedText || source.rawText}
              </Text>
              {source.rawText && source.rawText !== source.cleanedText && (
                <Text
                  size="xs"
                  c="dimmed"
                  style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                >
                  {t("jobResultsPage.tmTable.rawAs", {
                    defaultValue: "raw: {{value}}",
                    value: source.rawText,
                  })}
                </Text>
              )}
            </Stack>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Item-MP Listing */}
        <Table.Td>
          {target?.itemMpListing ? (
            <Text size="sm" lineClamp={3}>
              {target.itemMpListing}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* ItemMP ID */}
        <Table.Td>
          {target?.itemMpId != null ? (
            <Text size="sm" ff="monospace">
              {target.itemMpId}
            </Text>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* The catalogued value, and directly under it what the pipeline
            concluded ABOUT that value: "Exact" when the catalogue holds it,
            "Not found" when it does not. Read-only — changing it means picking
            another candidate.

            These two are the only findings technical-manual can produce, and
            both are claims about this cell's contents. Putting them in the
            Status column asked the reviewer to read a verdict two cells away
            from the thing it judges; the Status column now carries one axis
            only, what a HUMAN did. */}
        <Table.Td>
          {target?.valueDisplay ? (
            <Stack gap={2} align="flex-start">
              <Text size="sm" ta="left" lineClamp={2}>
                {target.valueDisplay} {target.unit && `(${target.unit})`}
              </Text>
              <Group gap={4} wrap="wrap" align="center">
                <RowFindingBadge row={row} />
                <TmScoreExamplesTooltip
                  jobId={jobId}
                  rowIndex={row.rowIndex}
                  rank={target.rank}
                  hasExamples={row.hasExamples}
                />
              </Group>
            </Stack>
          ) : (
            dimmed
          )}
        </Table.Td>

        {/* Status — the REVIEWER axis only. The pipeline's finding renders
            under the value it is about; see the value cell above. */}
        <RowStatusCell
          status={effectiveStatus}
          saveState={saveState}
          onRetrySave={onRetrySave}
        />

        <TmRowActions
          hasTopMatch={target !== null}
          actionsDisabled={actionsDisabled}
          isSaving={isSaving}
          isRejected={isRejected}
          onAccept={handleAccept}
          onReject={onReject}
          onRecover={onRecover}
          onOpenHistory={() => setHistoryOpen(true)}
        />

        {/* Candidate dropdown. No count: the list payload says only THAT
            candidates exist — they are fetched when this opens. */}
        <Table.Td>
          <Group gap={4} wrap="nowrap" justify="center">
            <Tooltip
              label={
                expanded
                  ? t("jobResultsPage.actions.hideAlternatives", {
                      defaultValue: "Hide alternatives",
                    })
                  : t("jobResultsPage.actions.showAlternatives", {
                      defaultValue: "Show other candidates",
                    })
              }
            >
              <ActionIcon
                size="sm"
                variant="transparent"
                color="blue"
                onClick={onToggleExpand}
                disabled={!row.hasAlternative || isFailed}
                aria-label={t("jobResultsPage.actions.showAlternatives", {
                  defaultValue: "Show other candidates",
                })}
              >
                {expanded ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>

      <RevisionHistoryDrawer
        opened={historyOpen}
        jobId={jobId}
        rowIndex={row.rowIndex}
        onRestored={onRowRestored}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

export default TmJobResultsTableRow;
